import React, { useState, useEffect, useCallback, useRef } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import debounce from "lodash/debounce";
import { Select, Button, Form, Input, InputNumber, Typography, Spin, Empty, message, Tag, Divider, Alert } from "antd";
import { SaveOutlined, FileTextOutlined } from "@ant-design/icons";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { PatientTreatmentPlan, TreatmentNote, TreatmentNoteTemplate } from "../../../../types/healthcare/TreatmentPlanTypes";
import { Appointment } from "../../../../types/appointments/AppointmentTypes";
import { fetchPatientNotes, fetchNotesByAppointment, fetchNoteTemplates, createNote, updateNote } from "../../../../api/healthcare/TreatmentPlanApi";

const { Text } = Typography;
const { TextArea } = Input;

interface ConsultTreatmentNotePanelProps {
	/** The patient's active treatment plan, or null if none */
	plan: PatientTreatmentPlan | null;
	appointment: Appointment;
	patient: Patient;
}

// ─── Dynamic form field renderer ──────────────────────────────────────────────
interface FieldDef {
	id: string;
	type: "heading" | "text" | "textarea" | "number" | "select" | "input";
	label: string;
	required?: boolean;
	placeholder?: string;
	options?: string[];
	rows?: number;
}

const NoteField: React.FC<{
	field: FieldDef;
	value: any;
	onChange: (v: any) => void;
	disabled?: boolean;
}> = ({ field, value, onChange, disabled }) => {
	switch (field.type) {
		case "textarea":
			return (
				<TextArea
					rows={field.rows ?? 4}
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={field.placeholder}
					disabled={disabled}
					style={{ resize: "vertical" }}
				/>
			);
		case "number":
			return (
				<InputNumber
					value={value}
					onChange={onChange}
					placeholder={field.placeholder}
					disabled={disabled}
					style={{ width: "100%" }}
				/>
			);
		case "select":
			return (
				<Select
					value={value}
					onChange={onChange}
					placeholder={field.placeholder ?? "Select…"}
					disabled={disabled}
					style={{ width: "100%" }}
					options={(field.options ?? []).map((o) => ({ value: o, label: o }))}
				/>
			);
		default:
			return (
				<Input
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
					placeholder={field.placeholder}
					disabled={disabled}
				/>
			);
	}
};

// ─── Main panel ───────────────────────────────────────────────────────────────
const ConsultTreatmentNotePanel: React.FC<ConsultTreatmentNotePanelProps> = ({ plan, appointment, patient }) => {
	const user = getSessionUser();

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [note, setNote] = useState<TreatmentNote | null>(null);
	const [templates, setTemplates] = useState<TreatmentNoteTemplate[]>([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>();
	const [responses, setResponses] = useState<Record<string, any>>({});
	const [dirty, setDirty] = useState(false);

	// Ref for debounced auto-save so we don't leak stale closures
	const saveRef = useRef<(r: Record<string, any>, n: TreatmentNote | null, tplId: number | undefined) => void>();

	const doSave = useCallback(
		async (r: Record<string, any>, currentNote: TreatmentNote | null, tplId: number | undefined) => {
			if (!plan || !tplId) return;
			setSaving(true);
			try {
				const payload = {
					patient_id: patient.id,
					practitioner_id: appointment.practitioner_id,
					patient_treatment_plan_id: plan.id,
					appointment_id: appointment.id,
					treatment_note_template_id: tplId,
					responses: r,
					status: "draft" as const,
				};
				if (currentNote) {
					const updated = await updateNote(currentNote.id, { responses: r, status: "draft" });
					setNote(updated);
				} else {
					const created = await createNote(payload);
					setNote(created);
				}
				setDirty(false);
			} catch {
				message.error("Failed to save note");
			} finally {
				setSaving(false);
			}
		},
		[plan, patient.id, appointment.id, user.company],
	);

	// Debounced auto-save (2 s after last keystroke)
	const debouncedSave = useRef(
		debounce((r: Record<string, any>, n: TreatmentNote | null, tplId: number | undefined) => {
			saveRef.current?.(r, n, tplId);
		}, 2000),
	).current;

	// Keep ref up-to-date so the debounce closure always calls the latest version
	useEffect(() => {
		saveRef.current = doSave;
	}, [doSave]);

	// ── Initial load ────────────────────────────────────────────────────────────
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			try {
				const tpls = await fetchNoteTemplates();
				if (cancelled) return;
				setTemplates(tpls);

				if (plan) {
					// First check for a note directly linked to this appointment
					const [appointmentNotes, planNotes] = await Promise.all([
						fetchNotesByAppointment(appointment.id),
						fetchPatientNotes(plan.id),
					]);
					if (cancelled) return;
					// Priority: note linked to this appointment > most recent draft on the plan
					const existing =
						appointmentNotes[0] ??
						planNotes.find((n) => n.appointment_id === appointment.id) ??
						planNotes.find((n) => n.status === "draft") ??
						null;
					if (existing) {
						setNote(existing);
						setResponses(existing.responses ?? {});
						setSelectedTemplateId(existing.treatment_note_template_id);
					} else {
						// Auto-select the note template from the current appointment stage config
						const stageConfig = plan.current_stage?.configuration as Record<string, any> | undefined;
						const stageNoteTemplateId = stageConfig?.note_template_id as number | undefined;
						if (stageNoteTemplateId) {
							setSelectedTemplateId(stageNoteTemplateId);
						}
					}
				}
			} catch {
				message.error("Failed to load treatment plan notes");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [plan?.id, appointment.id, user.company]);

	// ── Derived state ───────────────────────────────────────────────────────────
	const activeTemplate = templates.find((t) => t.id === selectedTemplateId);
	const fields: FieldDef[] = Array.isArray(activeTemplate?.structure) ? (activeTemplate!.structure as unknown as FieldDef[]) : [];
	const isReadOnly = appointment.status === "Completed";

	const handleFieldChange = (key: string, value: any) => {
		if (isReadOnly) return;
		const updated = { ...responses, [key]: value };
		setResponses(updated);
		setDirty(true);
		debouncedSave(updated, note, selectedTemplateId);
	};

	const handleManualSave = () => {
		debouncedSave.cancel();
		doSave(responses, note, selectedTemplateId);
	};

	const handleTemplateSelect = (id: number) => {
		setSelectedTemplateId(id);
		// Pre-populate responses with empty values for all fields in the new template
		const tpl = templates.find((t) => t.id === id);
		const tplFields: FieldDef[] = Array.isArray(tpl?.structure) ? (tpl!.structure as unknown as FieldDef[]) : [];
		const initial: Record<string, any> = {};
		tplFields.forEach((f) => {
			if (f.type !== "heading") initial[f.id] = responses[f.id] ?? "";
		});
		setResponses(initial);
		setDirty(true);
	};

	// ── Render: no plan ─────────────────────────────────────────────────────────
	if (!plan) {
		return (
			<div style={{ padding: 16 }}>
				<Empty
					image={<FileTextOutlined style={{ fontSize: 36, color: "#bfbfbf" }} />}
					description={
						<Text type="secondary" style={{ fontSize: 13 }}>
							No active treatment plan — assign one to enable plan notes
						</Text>
					}
				/>
			</div>
		);
	}

	// ── Render: loading ─────────────────────────────────────────────────────────
	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: 32 }}>
				<Spin />
			</div>
		);
	}

	// ── Render ──────────────────────────────────────────────────────────────────
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "8px 0" }}>
			{/* Plan context banner */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "6px 12px",
					background: "#f0f7ff",
					borderRadius: 6,
					border: "1px solid #bae0ff",
				}}
			>
				<div>
					<Text strong style={{ fontSize: 12 }}>
						{plan.template?.name ?? `Plan #${plan.id}`}
					</Text>
				</div>
				<Tag color="blue" style={{ fontSize: 10 }}>
					{plan.status}
				</Tag>
			</div>

			{/* Template selector — show when no template selected or no note exists yet */}
		{(!note || !note.treatment_note_template_id) && (
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
						Select a note template to structure this consultation note:
					</Text>
					<Select
						value={selectedTemplateId}
						onChange={handleTemplateSelect}
						placeholder="Choose a note template…"
						showSearch
						optionFilterProp="label"
						style={{ width: "100%" }}
						loading={loading}
						allowClear
						options={templates.map((t) => ({
							value: t.id,
							label: t.name,
							title: t.description,
						}))}
					/>
				</div>
			)}

			{/* Template switcher when note already exists */}
		{note?.treatment_note_template_id && (
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
						Template:
					</Text>
					<Select
						value={selectedTemplateId}
						onChange={handleTemplateSelect}
						showSearch
						optionFilterProp="label"
						size="small"
						style={{ flex: 1 }}
						disabled={isReadOnly}
						options={templates.map((t) => ({ value: t.id, label: t.name }))}
					/>
				</div>
			)}

			{/* Dynamic form */}
			{selectedTemplateId && fields.length > 0 && (
				<Form layout="vertical" size="small">
				{fields.map((field) =>
					field.type === "heading" ? (
						<div key={field.id} style={{ marginBottom: 8, marginTop: 4 }}>
							<Text strong style={{ fontSize: 13 }}>{field.label}</Text>
							<Divider style={{ margin: "4px 0 8px" }} />
						</div>
					) : (
						<Form.Item
							key={field.id}
							label={field.label}
							required={field.required}
							style={{ marginBottom: 10 }}
						>
							<NoteField
								field={field}
								value={responses[field.id]}
								onChange={(v) => handleFieldChange(field.id, v)}
								disabled={isReadOnly}
							/>
						</Form.Item>
					)
				)}
			</Form>
			)}

			{/* Free-text fallback when template has no structured fields */}
			{selectedTemplateId && fields.length === 0 && (
				<Form.Item label="Notes">
					<TextArea
						rows={8}
						value={responses.__freetext ?? ""}
						onChange={(e) => handleFieldChange("__freetext", e.target.value)}
						placeholder="Enter consultation notes…"
						disabled={isReadOnly}
					/>
				</Form.Item>
			)}

			{/* No template selected yet: just show a plain text-area */}
			{!selectedTemplateId && (
				<Form.Item label="Free-text Note">
					<TextArea
						rows={8}
						value={responses.__freetext ?? ""}
						onChange={(e) => handleFieldChange("__freetext", e.target.value)}
						placeholder="Enter free-text notes, or select a template above for a structured form…"
						disabled={isReadOnly}
						data-testid="consultation-note-freetext"
					/>
				</Form.Item>
			)}

			{isReadOnly && (
				<Alert type="info" showIcon message="This consultation is completed — notes are read-only." style={{ fontSize: 12 }} />
			)}

			{/* Save row */}
			{!isReadOnly && (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					{dirty ? (
						<Text type="secondary" style={{ fontSize: 11 }}>
							Unsaved changes — auto-saving…
						</Text>
					) : note ? (
						<Text type="secondary" style={{ fontSize: 11 }}>
							Saved {note.updated_at ? `· ${new Date(note.updated_at).toLocaleTimeString()}` : ""}
						</Text>
					) : (
						<span />
					)}
					<Button
						size="small"
						type="primary"
						icon={<SaveOutlined />}
						loading={saving}
						onClick={handleManualSave}
						disabled={!dirty && !!note}
						data-testid="consultation-note-save-btn"
					>
						Save Note
					</Button>
				</div>
			)}
		</div>
	);
};

export default ConsultTreatmentNotePanel;
